import { useState } from 'react';
import { ChevronDown, ChevronRight, FileCode2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useNodeManifest } from '@/hooks/queries/workflow/use-node-manifest';
import {
  getCustomCodePin,
  manifestLines,
} from '@/pages/workflow/helper/custom-code-manifest';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import type { NodeStruct } from '@/types/node-types';

interface CustomCodeManifestRowsProps {
  nodeId: string;
  struct: NodeStruct;
}

/**
 * A small "Manifest" disclosure under a Custom Code node's port rows: one
 * compact line per input and output declared in the pinned `node.json`.
 * Collapsed by default so the node body stays short; the full tables live
 * in the config panel's Script card. Renders nothing until there is a
 * saved workflow, a pinned script and a readable manifest.
 */
export function CustomCodeManifestRows({
  nodeId,
  struct,
}: CustomCodeManifestRowsProps) {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const workflowId = useWorkflowStore((state) => state.currentWorkflow?.id);
  const pin = getCustomCodePin(struct);
  const query = useNodeManifest(
    orgId,
    workflowId,
    nodeId,
    pin.scriptPath,
    pin.scriptSha,
    pin.pinned,
  );
  const [open, setOpen] = useState(false);

  const summary = query.data?.summary;
  if (!summary || !summary.valid) return null;
  const { lines, hidden } = manifestLines(summary);
  if (!lines.length) return null;
  const problems = summary.problems.filter((p) => p.severity === 'error');

  return (
    <div
      className='mt-2 border-t border-border/60 pt-1 text-[11px]'
      data-testid='custom-code-manifest-rows'
    >
      <button
        type='button'
        className='nodrag flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-muted-foreground hover:bg-muted/60 hover:text-foreground'
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        data-testid='custom-code-manifest-toggle'
      >
        {open ? (
          <ChevronDown className='size-3 shrink-0' />
        ) : (
          <ChevronRight className='size-3 shrink-0' />
        )}
        <FileCode2 className='size-3 shrink-0' />
        <span className='truncate'>
          Manifest: {summary.inputs.length || (summary.input_value ? 1 : 0)} in,{' '}
          {summary.outputs.length || (summary.output_value ? 1 : 0)} out
          {problems.length ? ` · ${problems.length} problem` : ''}
          {problems.length > 1 ? 's' : ''}
        </span>
      </button>
      {open && (
        <ul
          className='mt-0.5 space-y-0.5 px-1 pb-0.5'
          data-testid='custom-code-manifest-lines'
        >
          {lines.map((line) => (
            <li
              key={`${line.direction}:${line.text}`}
              className='flex gap-1 truncate font-mono text-muted-foreground'
              title={line.text}
            >
              <span
                className={cn(
                  'w-6 shrink-0 text-right',
                  line.direction === 'in'
                    ? 'text-sky-600 dark:text-sky-400'
                    : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {line.direction}:
              </span>
              <span className='truncate'>{line.text}</span>
            </li>
          ))}
          {hidden > 0 && (
            <li className='pl-7 text-muted-foreground'>
              +{hidden} more in the panel
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
